import { useEffect, useMemo, useRef, useState } from "react";
// Using native browser Speech Recognition API instead of react-speech-recognition
import axios from "axios";
import { useTranslation } from "react-i18next";
import { parseAttendanceCommandWithAI } from "../../utils/aiCommandParser";
import { getCommandCatalogForPage } from "../../utils/voiceCommandsCatalog";
import {
  getLanguageCode,
  getSpeechRecognitionLanguage,
  hasPlatformWakePrefix,
  isCleanIntent,
  isConfirmIntent,
  isExportAttendanceIntent,
  isHelpIntent,
  isRedoIntent,
  isUndoIntent,
} from "../../utils/voiceIntents";
import "./VoiceCommandWidgetV2.css";
import WidgetLayout from "./components/WidgetLayout";
import { useAvatarVoiceReply } from "./components/AvatarVoiceReply";

const VoiceCommandWidgetV2 = ({
  onVoiceCommand,
  isEnabled = true,
  page = "attendance",
  studentList = [], // Add student list for better name matching
  user,
}) => {
  // Get i18n instance and current language
  const { i18n, t } = useTranslation();

  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState("idle"); // 'idle', 'paused', 'listening', 'transcribing', 'confirming', 'processing', 'success', 'error'
  const [transcript, setTranscript] = useState("");
  const [pendingNav, setPendingNav] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [isNoProcessing, setIsNoProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - 350,
    y: 100,
  });
  const [micPosition, setMicPosition] = useState(() => {
    const buttonSize = 60;
    const margin = 20;
    return {
      x: Math.max(margin, window.innerWidth - buttonSize - margin),
      y: Math.max(margin, window.innerHeight - buttonSize - margin),
    };
  });
  const widgetRef = useRef(null);
  const micButtonRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // If a previous page requested attendance export after navigation, run it here
  useEffect(() => {
    try {
      if (page === "attendance") {
        const shouldExport =
          localStorage.getItem("acv_export_after_nav") === "1";
        if (shouldExport) {
          localStorage.removeItem("acv_export_after_nav");
          const el = document.querySelector(
            '[data-acv-tag="attendance-export"]'
          );
          if (el) {
            el.click();
            setCurrentStep("success");
            setFeedbackMessage("Attendance export started");
          }
        }
      }
    } catch (e) {
      /* ignore */
    }
  }, [page]);

  useEffect(() => {
    try {
      const openFull = localStorage.getItem("acv_open_full_on_arrival") === "1";
      if (openFull) {
        localStorage.removeItem("acv_open_full_on_arrival");
        try {
          localStorage.setItem("acv_compact_mode", "0");
        } catch (e) {}
        setCompactMode(false);
        setIsVisible(true);
        setCurrentStep("listening");
        setFeedbackMessage("");
        clearTranscript();
        setIsNoProcessing(false);
        const wakeActive = localStorage.getItem("acv_wake_active") === "1";
        if (wakeActive) {
          setAwaitWakeWord(false);
          awaitWakeWordRef.current = false;
        } else {
          setAwaitWakeWord(true);
          awaitWakeWordRef.current = true;
        }
        wakePromptPlayedRef.current = false;
        startListening(true);
        if (voiceReplyEnabledRef.current) {
          try {
            const friendly = String(page || "");
            speakWithPause(getVoicePhrase("openedTab", { tab: friendly }));
          } catch (e) {}
        }
      } else {
        // If we changed page and were showing a navigating message, switch to regular listening view
        if (isVisibleRef.current && currentStepRef.current === "processing" && /^Navigating to /i.test(String(feedbackMessage || ""))) {
          setCurrentStep("listening");
          setFeedbackMessage("");
          clearTranscript();
        }
      }
    } catch (e) {}
  }, [page]);

  // Refs to track current state for auto-restart logic
  const currentStepRef = useRef(currentStep);
  const isVisibleRef = useRef(isVisible);
  const listeningRef = useRef(false);
  // Track explicit programmatic stops to suppress 'aborted' error noise
  const explicitStopRef = useRef(false);
  // Health check interval for continuous listening
  const healthCheckIntervalRef = useRef(null);
  // Timestamp of last successful command to suppress immediate follow-up errors
  const lastSuccessAtRef = useRef(0);
  // Timestamps to monitor recognition health
  const lastResultAtRef = useRef(0);
  const lastStartAtRef = useRef(0);
  const lastRestartAttemptAtRef = useRef(0);
  // Track consecutive restart failures to trigger recreation
  const consecutiveRestartFailuresRef = useRef(0);
  const recognitionRecreationTimeoutRef = useRef(null);
  const wakePromptPlayedRef = useRef(false);
  const lastTTSAtRef = useRef(0);
  const exportTTSGuardRef = useRef(false);
  const lastHelpTTSAtRef = useRef(0);

  // Last known attendance dataset (persisted by Attendance page)
  const [lastAttendanceDataset, setLastAttendanceDataset] = useState(null);
  // Internal flag to avoid duplicate background fetches
  const isFetchingExportDataRef = useRef(false);

  // Load last attendance dataset from localStorage so export can work on any tab
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("acv_last_attendance_records");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          setLastAttendanceDataset(parsed);
        } else {
          setLastAttendanceDataset(null);
        }
      } else {
        setLastAttendanceDataset(null);
      }
    } catch (e) {
      setLastAttendanceDataset(null);
    }
  }, [page, isVisible]);

  // Wake word configuration/state
  const wakeWordFromStorage = () => {
    try {
      const saved = localStorage.getItem("acv_wake_word");
      if (!saved) return "Assistant";
      return "Assistant";
    } catch (e) {
      return "Assistant";
    }
  };
  const [wakeWord, setWakeWord] = useState(wakeWordFromStorage());
  const [awaitWakeWord, setAwaitWakeWord] = useState(false);
  const awaitWakeWordRef = useRef(awaitWakeWord);
  const shouldAutoRestartRef = useRef(false);
  const autoConfirmFromStorage = () => {
    try {
      const v = localStorage.getItem("acv_auto_confirm");
      return v ? v === "1" : false; // default OFF to keep Confirm button visible
    } catch (e) {
      return false;
    }
  };
  const [autoConfirm, setAutoConfirm] = useState(autoConfirmFromStorage());

  // API base URL from environment
  const url = process.env.REACT_APP_EP_URL;

  const sanitizeSlug = (slug) => {
    if (!slug || typeof slug !== "string") return "";
    const stripPrefix = (str) => str.replace(/^\?*slug=/i, "");
    let cleaned = stripPrefix(slug.trim());
    try {
      cleaned = decodeURIComponent(cleaned);
    } catch (_err) {
      // ignore decode errors
    }
    cleaned = stripPrefix(cleaned);
    return cleaned.replace(/\//g, "");
  };

  const getSlugParam = () => {
    if (user?.slug) {
      return sanitizeSlug(user.slug);
    }

    if (typeof window !== "undefined") {
      const fromUrl =
        new URLSearchParams(window.location.search).get("slug") || "";
      if (fromUrl) {
        return sanitizeSlug(fromUrl);
      }

      const stored = window.localStorage?.getItem("slug");
      if (stored) {
        return sanitizeSlug(stored);
      }
    }

    return "";
  };

  const buildCommandLogsEndpoint = (extraParams = {}) => {
    if (!url) return "";
    const params = new URLSearchParams();
    const slug = getSlugParam();
    if (slug) {
      params.append("slug", slug);
    }
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });
    const qs = params.toString();
    return `${url}/api/command-logs${qs ? `?${qs}` : ""}`;
  };

  // Build auth headers for API calls
  const getAuthHeaders = () => {
    try {
      const token = user?.accessToken;
      if (!token) return {};
      return { Authorization: `Bearer ${token}` };
    } catch (e) {
      return {};
    }
  };

  // Track export flow to avoid flashing errors when export is about to start
  const exportStartedRef = useRef(false);
  const exportErrorTimeoutRef = useRef(null);

  // Cleanup any pending export error timers on unmount
  useEffect(() => {
    return () => {
      if (exportErrorTimeoutRef.current) {
        clearTimeout(exportErrorTimeoutRef.current);
        exportErrorTimeoutRef.current = null;
      }
      if (recognitionRecreationTimeoutRef.current) {
        clearTimeout(recognitionRecreationTimeoutRef.current);
        recognitionRecreationTimeoutRef.current = null;
      }
    };
  }, []);

  // Attempt to fetch any available lesson records for today without navigating
  const ensureGlobalExportDataset = async () => {
    try {
      // If we already have a dataset, nothing to do
      if (
        Array.isArray(lastAttendanceDataset) &&
        lastAttendanceDataset.length
      ) {
        return lastAttendanceDataset;
      }
      if (isFetchingExportDataRef.current) return null;
      isFetchingExportDataRef.current = true;

      const slug = getSlugParam();
      const slugQuery = slug ? `?slug=${encodeURIComponent(slug)}` : "";
      if (!url || !slugQuery) {
        isFetchingExportDataRef.current = false;
        return null;
      }

      const todayIso = new Date(new Date().setHours(0, 0, 0, 0)).toISOString();
      // Fetch lessons for today (no filters) similar to Attendance.fetchLessons
      const lessonsResp = await axios.post(
        `${url}/api/lession/get-lession-by-teacher${slugQuery}`,
        { ClassName: null, TeacherEmailAlias: null, LessonDate: todayIso },
        { headers: getAuthHeaders() }
      );
      const showLesson = (lessonsResp?.data?.result || []).sort((a, b) =>
        String(a?.StartTime || "").toLowerCase() >
        String(b?.StartTime || "").toLowerCase()
          ? 1
          : -1
      );
      if (!showLesson.length) {
        isFetchingExportDataRef.current = false;
        return null;
      }
      const targetLesson = showLesson[0];
      const lessonKey = targetLesson?.LessonId || targetLesson?.Title;
      if (!lessonKey) {
        isFetchingExportDataRef.current = false;
        return null;
      }

      // Fetch records for the chosen lesson (like Attendance.fetchRecordsByLesson)
      const recResp = await axios.get(
        `${url}/api/record/get-record-by-lession/${lessonKey}${slugQuery}`,
        { headers: getAuthHeaders() }
      );
      const records = recResp?.data?.result || [];
      if (Array.isArray(records) && records.length) {
        try {
          window.localStorage.setItem(
            "acv_last_attendance_records",
            JSON.stringify(records)
          );
        } catch (e) {}
        setLastAttendanceDataset(records);
        isFetchingExportDataRef.current = false;
        return records;
      }

      // If no records returned, still set empty to avoid loops
      setLastAttendanceDataset(null);
      isFetchingExportDataRef.current = false;
      return null;
    } catch (e) {
      isFetchingExportDataRef.current = false;
      return null;
    }
  };

  // Health check function to ensure continuous listening
  const startHealthCheck = () => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }

    healthCheckIntervalRef.current = setInterval(() => {
      const dialogsOpen =
        showCommandLogRef.current || showCommandHelpRef.current;
      const now = Date.now();

      // Case 1: Should be listening but recognition is not running
      if (
        isVisibleRef.current &&
        shouldAutoRestartRef.current &&
        (currentStepRef.current === "listening" ||
          currentStepRef.current === "transcribing" ||
          currentStepRef.current === "idle") &&
        !listeningRef.current &&
        !dialogsOpen &&
        speechRecognition
      ) {
        try {
          console.log(
            "🔄 Health check: Recognition not running but should be. Starting..."
          );
          speechRecognition.start();
          lastRestartAttemptAtRef.current = now;
        } catch (error) {
          consecutiveRestartFailuresRef.current += 1;
          console.warn(
            "⚠️ Health check: Failed to restart speech recognition:",
            error,
            "Consecutive failures:",
            consecutiveRestartFailuresRef.current
          );

          // If we've failed multiple times, the recognition instance might be dead
          if (consecutiveRestartFailuresRef.current >= 3) {
            console.error(
              "❌ Health check: Multiple restart failures. Recognition instance may be dead."
            );
            // Force a language change to trigger recreation (handled by useEffect)
            // This is a workaround - the recognition will be recreated on next language change
          }
        }
      }

      // Case 2: Watchdog - supposedly listening but haven't received results for a while
      if (
        isVisibleRef.current &&
        shouldAutoRestartRef.current &&
        (currentStepRef.current === "listening" ||
          currentStepRef.current === "transcribing" ||
          currentStepRef.current === "idle") &&
        listeningRef.current &&
        !dialogsOpen &&
        speechRecognition
      ) {
        const lastActivity =
          lastResultAtRef.current || lastStartAtRef.current || 0;
        const staleForMs = lastActivity ? now - lastActivity : 0;
        const recentlyRestarted =
          now - (lastRestartAttemptAtRef.current || 0) < 4000;
        const timeSinceStart = now - (lastStartAtRef.current || 0);

        // If no interim/final results have come in for 10s while listening, reset recognition
        // Also check if recognition has been running for more than 60s without results (browser timeout)
        const isStale = staleForMs > 10000 && !recentlyRestarted;
        const isLikelyTimeout = timeSinceStart > 60000 && staleForMs > 5000;

        if (isStale || isLikelyTimeout) {
          try {
            console.warn(
              "🔄 Health check: recognition appears stale (",
              staleForMs,
              "ms without results, running for",
              timeSinceStart,
              "ms). Forcing reset."
            );
            lastRestartAttemptAtRef.current = now;
            // Do not mark explicitStopRef; we want auto-restart to kick in via onend
            speechRecognition.stop();
            // Fallback: if onend doesn't fire or restart fails, try a direct restart
            setTimeout(() => {
              if (
                shouldAutoRestartRef.current &&
                isVisibleRef.current &&
                (currentStepRef.current === "listening" ||
                  currentStepRef.current === "transcribing" ||
                  currentStepRef.current === "idle") &&
                !listeningRef.current &&
                !dialogsOpen
              ) {
                try {
                  console.log("🔄 Health check: Fallback restart attempt...");
                  speechRecognition.start();
                  consecutiveRestartFailuresRef.current = 0;
                } catch (e1) {
                  consecutiveRestartFailuresRef.current += 1;
                  console.warn(
                    "⚠️ Watchdog fallback: immediate restart failed, retrying…",
                    e1,
                    "Consecutive failures:",
                    consecutiveRestartFailuresRef.current
                  );
                  setTimeout(() => {
                    if (
                      shouldAutoRestartRef.current &&
                      isVisibleRef.current &&
                      !listeningRef.current &&
                      !dialogsOpen
                    ) {
                      try {
                        speechRecognition.start();
                        consecutiveRestartFailuresRef.current = 0;
                      } catch (e2) {
                        consecutiveRestartFailuresRef.current += 1;
                        console.error(
                          "❌ Watchdog fallback: restart failed after retry:",
                          e2,
                          "Consecutive failures:",
                          consecutiveRestartFailuresRef.current
                        );
                      }
                    }
                  }, 1500);
                }
              }
            }, 800); // Reduced delay for faster recovery
          } catch (e) {
            console.warn("⚠️ Health check: Failed to stop for reset:", e);
          }
        }
      }
    }, 3000); // Check every 3 seconds (more frequent for better responsiveness)
  };

  const stopHealthCheck = () => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
  };

  // Persist a single command log entry to server (best-effort)
  const persistCommandLogToServer = async (entry) => {
    try {
      const token = user?.accessToken;
      if (!getSlugParam() || !token || !url) return; // missing auth context

      const endpoint = buildCommandLogsEndpoint();
      if (!endpoint) return;
      await axios({
        method: "POST",
        url: endpoint,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          timestamp: entry.timestamp,
          command: entry.command,
          type: entry.type,
          studentName: entry.studentName,
          success: entry.success,
          message: entry.message,
          page: entry.page,
        }),
      });
    } catch (err) {
      console.warn("Failed to persist command log to server:", err);
    }
  };

  // Clear all command logs on server for current slug (best-effort)
  const clearServerCommandLogs = async () => {
    try {
      const token = user?.accessToken;
      if (!getSlugParam() || !token || !url) return; // missing auth context

      const endpoint = buildCommandLogsEndpoint();
      if (!endpoint) return;
      await axios({
        method: "DELETE",
        url: endpoint,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn("Failed to clear command logs on server:", err);
    }
  };

  // Fetch command logs from server for current slug
  const fetchCommandLogsFromServer = async (limit = 100) => {
    try {
      const token = user?.accessToken;
      if (!getSlugParam() || !token || !url) return; // missing auth context

      const endpoint = buildCommandLogsEndpoint({ limit: String(limit) });
      if (!endpoint) return;
      const response = await axios({
        method: "GET",
        url: endpoint,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const logs = response?.data?.result || [];
      setCommandLog(logs);
    } catch (err) {
      console.warn("Failed to fetch command logs from server:", err);
    }
  };

  // Additional preferences
  const showCommandsFromStorage = () => {
    try {
      const v = localStorage.getItem("acv_show_commands");
      return v ? v === "1" : true;
    } catch (e) {
      return true;
    }
  };
  const voiceReplyFromStorage = () => {
    try {
      const v = localStorage.getItem("acv_voice_reply");
      return v ? v === "1" : false;
    } catch (e) {
      return false;
    }
  };
  const [showCommands, setShowCommands] = useState(showCommandsFromStorage());
  const [voiceReplyEnabled, setVoiceReplyEnabled] = useState(
    voiceReplyFromStorage()
  );
  const voiceReplyEnabledRef = useRef(voiceReplyEnabled);
  useEffect(() => {
    voiceReplyEnabledRef.current = voiceReplyEnabled;
  }, [voiceReplyEnabled]);
  const compactModeFromStorage = () => {
    try {
      const v = localStorage.getItem("acv_compact_mode");
      return v ? v === "1" : true; // default ON to show compact view (like first image)
    } catch (e) {
      return true;
    }
  };
  const [compactMode, setCompactMode] = useState(compactModeFromStorage());
  const commandProcessTimeoutRef = useRef(null);

  const toggleShowCommands = () => {
    setShowCommands((value) => {
      const next = !value;
      try {
        localStorage.setItem("acv_show_commands", next ? "1" : "0");
      } catch (e) {}
      return next;
    });
  };

  const toggleVoiceReply = () => {
    setVoiceReplyEnabled((value) => {
      const next = !value;
      try {
        localStorage.setItem("acv_voice_reply", next ? "1" : "0");
      } catch (e) {}
      return next;
    });
  };

  const { speakReply, buildVoiceReply } = useAvatarVoiceReply({
    enabled: voiceReplyEnabled,
    language: i18n.language,
  });

  const getVoicePhrase = (key, params = {}) => {
    const base = getLanguageCode(i18n.language);
    const map = {
      whatHelp: {
        en: "What can I help you?",
        es: "¿En qué puedo ayudarte?",
        pt: "Como posso ajudar?",
        fr: "Comment puis-je vous aider ?",
        de: "Womit kann ich helfen?",
        hi: "मैं आपकी किसमें मदद कर सकता हूँ?",
        it: "Come posso aiutarti?",
        ja: "どのようにお手伝いできますか？",
      },
      helpIntro: {
        en: "Here are some commands you can say.",
        es: "Aquí tienes algunos comandos que puedes decir.",
        pt: "Aqui estão alguns comandos que você pode dizer.",
        fr: "Voici quelques commandes que vous pouvez dire.",
        de: "Hier sind einige Befehle, die Sie sagen können.",
        hi: "आप कुछ ऐसे कमांड कह सकते हैं।",
        it: "Ecco alcuni comandi che puoi dire.",
        ja: "いくつかの音声コマンドをご紹介します。",
      },
      openedTab: {
        en: "Opened {tab}",
        es: "Abierto {tab}",
        pt: "Aberto {tab}",
        fr: "Ouvert {tab}",
        de: "Geöffnet {tab}",
        hi: "{tab} खोला गया",
        it: "Aperto {tab}",
        ja: "{tab} を開きました",
      },
      alreadyOnTab: {
        en: "Already on {tab}",
        es: "Ya estás en {tab}",
        pt: "Já está em {tab}",
        fr: "Déjà sur {tab}",
        de: "Bereits auf {tab}",
        hi: "पहले से {tab} पर हैं",
        it: "Sei già su {tab}",
        ja: "すでに {tab} にいます",
      },
      navigatingToTab: {
        en: "Navigating to {tab}",
        es: "Navegando a {tab}",
        pt: "Navegando para {tab}",
        fr: "Navigation vers {tab}",
        de: "Navigiere zu {tab}",
        hi: "{tab} पर जा रहा हूँ",
        it: "Navigando verso {tab}",
        ja: "{tab} に移動しています",
      },
      attendanceExportStarted: {
        en: "Attendance export started",
        es: "Exportación de asistencia iniciada",
        pt: "Exportação de presença iniciada",
        fr: "Export de présence démarré",
        de: "Anwesenheitsexport gestartet",
        hi: "उपस्थिति निर्यात शुरू हुआ",
        it: "Esportazione presenze avviata",
        ja: "出席のエクスポートを開始しました",
      },
      exportFailed: {
        en: "Failed to prepare export dataset",
        es: "Error al preparar el conjunto de datos de exportación",
        pt: "Falha ao preparar o conjunto de dados para exportação",
        fr: "Échec de préparation des données d'export",
        de: "Vorbereitung des Export-Datensatzes fehlgeschlagen",
        hi: "निर्यात डेटा सेट तैयार करने में विफल",
        it: "Impossibile preparare il dataset di esportazione",
        ja: "エクスポートデータセットの準備に失敗しました",
      },
    };
    const raw = (map[key] || {})[base] || (map[key] || {}).en || "";
    return String(raw).replace(/\{(\w+)\}/g, (_, p) =>
      params && params[p] != null ? String(params[p]) : `{${p}}`
    );
  };

  const toggleAutoConfirm = () => {
    setAutoConfirm((value) => {
      const next = !value;
      try {
        localStorage.setItem("acv_auto_confirm", next ? "1" : "0");
      } catch (e) {}
      return next;
    });
  };

  const toggleCompactMode = () => {
    setCompactMode((value) => {
      const next = !value;
      try {
        localStorage.setItem("acv_compact_mode", next ? "1" : "0");
      } catch (e) {}
      return next;
    });
  };

  // Command Log state management
  const [commandLog, setCommandLog] = useState([]);
  const [showCommandLog, setShowCommandLog] = useState(false);
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const [logFilterType, setLogFilterType] = useState("all");
  const commandLogContentRef = useRef(null);
  // Visibility refs to safely read latest state inside async handlers (e.g., onerror)
  const showCommandLogRef = useRef(false);
  const showCommandHelpRef = useRef(false);
  // Command Help modal state
  const [showCommandHelp, setShowCommandHelp] = useState(false);

  const openCommandHelp = async () => {
    await exitFullscreenIfNeeded();
    setShowCommandHelp(true);
  };

  const openCommandLog = async () => {
    await exitFullscreenIfNeeded();
    setShowCommandLog(true);
  };

  // Keep visibility refs in sync with dialog open state
  useEffect(() => {
    showCommandLogRef.current = showCommandLog;
  }, [showCommandLog]);

  useEffect(() => {
    showCommandHelpRef.current = showCommandHelp;
  }, [showCommandHelp]);

  // Speech recognition core states (must be declared before effects that reference them)
  const [speechRecognition, setSpeechRecognition] = useState(null);
  const [
    browserSupportsSpeechRecognition,
    setBrowserSupportsSpeechRecognition,
  ] = useState(false);
  const [listening, setListening] = useState(false);
  const [speechTranscript, setSpeechTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");

  // Stop previous recognition instance when language changes to avoid duplicate sessions
  useEffect(() => {
    return () => {
      try {
        explicitStopRef.current = true;
        if (speechRecognition) speechRecognition.stop();
      } catch (e) {
        /* noop */
      }
    };
  }, [i18n.language]);

  // Mic level visualization (Web Audio API)
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const analyserRef = useRef(null);
  const meterIntervalRef = useRef(null);
  const [meterBars, setMeterBars] = useState(Array(60).fill(8));

  // Track long-running whiteboard spin wheel operation
  const [isSpinWheelOpActive, setIsSpinWheelOpActive] = useState(false);
  const isSpinWheelOpActiveRef = useRef(false);
  useEffect(() => {
    isSpinWheelOpActiveRef.current = isSpinWheelOpActive;
  }, [isSpinWheelOpActive]);

  // Ensure fullscreen is exited before showing any modal/dialog
  const exitFullscreenIfNeeded = async () => {
    try {
      // Exit browser fullscreen if active
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (e) {
      // noop
    }

    try {
      // Signal Whiteboard to exit its CSS-based zoom/fullscreen mode
      window.postMessage({ type: "ZOOM_IN_OUT", isZoom: false }, "*");
    } catch (e) {
      // noop
    }

    // Small delay to allow layout/z-index to settle before opening modal
    await new Promise((res) => setTimeout(res, 100));
  };

  // Listen for spin wheel status events from Whiteboard
  useEffect(() => {
    const handler = (e) => {
      const status = e?.detail?.status;
      if (!status) return;
      if (status === "generating") {
        setCurrentStep("processing");
        setFeedbackMessage("Generating spin wheel…");
        setIsSpinWheelOpActive(true);
      } else if (status === "generated") {
        setCurrentStep("processing");
        setFeedbackMessage("Topics generated. Review and insert into wheel.");
        setIsSpinWheelOpActive(true);
      } else if (status === "inserted") {
        setCurrentStep("success");
        setFeedbackMessage("Spin wheel added to canvas.");
        setIsSpinWheelOpActive(false);
        if (localStorage.getItem("acv_auto_confirm") !== "1") {
          setTimeout(() => {
            setIsVisible(false);
            resetWidget();
          }, 3000);
        } else {
          setTimeout(() => {
            clearTranscript();
            setIsNoProcessing(false);
            setAwaitWakeWord(false);
            awaitWakeWordRef.current = false;
            setCurrentStep("listening");
            resumeListeningIfOpen(false);
          }, 800);
        }
      } else if (status === "error") {
        setCurrentStep("error");
        setFeedbackMessage("Failed to generate spin wheel.");
        setIsSpinWheelOpActive(false);
        if (localStorage.getItem("acv_auto_confirm") !== "1") {
          setTimeout(() => {
            setIsVisible(false);
            resetWidget();
          }, 5000);
        } else {
          setTimeout(() => {
            clearTranscript();
            setIsNoProcessing(false);
            setAwaitWakeWord(false);
            awaitWakeWordRef.current = false;
            setCurrentStep("listening");
            resumeListeningIfOpen(false);
          }, 1200);
        }
      }
    };
    window.addEventListener("ACV_SPINWHEEL_STATUS", handler);
    return () => window.removeEventListener("ACV_SPINWHEEL_STATUS", handler);
  }, []);

  const startMeter = async () => {
    try {
      if (!isVisibleRef.current) return;
      if (meterIntervalRef.current) return; // already running

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
      }

      if (!mediaStreamRef.current) {
        mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      }

      if (!analyserRef.current) {
        const ctx = audioContextRef.current;
        const source = ctx.createMediaStreamSource(mediaStreamRef.current);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.85;
        source.connect(analyser);
        analyserRef.current = analyser;
      }

      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Update meter at 100ms to avoid frequent main-thread work warnings
      meterIntervalRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(dataArray);
        const bars = new Array(60).fill(8);
        const step = Math.floor(bufferLength / 60) || 1;
        for (let i = 0; i < 60; i++) {
          const v = dataArray[i * step] - 128;
          const amp = Math.min(20, Math.max(4, Math.abs(v) / 2));
          bars[i] = amp;
        }
        setMeterBars(bars);
      }, 100);
    } catch (e) {
      console.warn("[WaveMeter] Failed to start meter:", e?.message);
    }
  };

  const stopMeter = () => {
    try {
      if (meterIntervalRef.current) {
        clearInterval(meterIntervalRef.current);
        meterIntervalRef.current = null;
      }
    } catch (e) {
      /* noop */
    }
  };

  // Pause the audio meter while the Command Log dialog is open to avoid
  // unnecessary timer work and potential click interference.
  useEffect(() => {
    if (showCommandLog) {
      stopMeter();
    } else if (listeningRef.current) {
      // Restart meter only if we are currently listening
      startMeter();
    }
  }, [showCommandLog]);

  // Also pause the audio meter while the Command Help dialog is open
  useEffect(() => {
    if (showCommandHelp) {
      stopMeter();
    } else if (listeningRef.current) {
      startMeter();
    }
  }, [showCommandHelp]);

  // Keep refs updated
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "acv_voice_widget_visible",
        isVisible ? "1" : "0"
      );
      if (!isVisible) {
        localStorage.setItem("acv_wake_active", "0");
      }
    } catch (e) {}
  }, [isVisible]);

  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  useEffect(() => {
    awaitWakeWordRef.current = awaitWakeWord;
  }, [awaitWakeWord]);

  useEffect(() => {
    try {
      const active = !awaitWakeWord && isVisible ? "1" : "0";
      localStorage.setItem("acv_wake_active", active);
    } catch (e) {}
  }, [awaitWakeWord, isVisible]);

  // When the wake word changes, re-arm wake detection if widget is visible
  useEffect(() => {
    try {
      localStorage.setItem("acv_wake_word", wakeWord);
    } catch (e) {}
    if (
      isVisible &&
      (currentStep === "listening" || currentStep === "transcribing")
    ) {
      setAwaitWakeWord(true);
      // Synchronously update ref to avoid race with recognition callbacks
      awaitWakeWordRef.current = true;
      setFeedbackMessage("");
      setTranscript("");
      setSpeechTranscript("");
      setInterimTranscript("");
      // Keep mic running; restart only if currently not listening
      if (!listening) startListening(false);
    }
  }, [wakeWord]);

  // Native browser Speech Recognition API

  // Initialize speech recognition - with i18n.language in dependency array to keep language in sync
  useEffect(() => {
    let recognition = null;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      recognition = new SpeechRecognition();
      recognition.continuous = true;
      // Enable interim results to keep the recognition session alive
      recognition.interimResults = true;

      // Force wake word detection in English, then use selected language for commands
      const currentLang = i18n.language || "en";
      const forWake = "en-US"; // wake word is always in English
      const commandLang = getSpeechRecognitionLanguage(currentLang);
      recognition.lang = awaitWakeWordRef.current ? forWake : commandLang;

      recognition.maxAlternatives = 1;
      // Expose recognition instance to component state for lifecycle control
      setSpeechRecognition(recognition);

      recognition.onstart = () => {
        listeningRef.current = true;
        setListening(true);
        try {
          lastStartAtRef.current = Date.now();
          console.log(
            "✅ Recognition started successfully at",
            new Date().toISOString()
          );
          consecutiveRestartFailuresRef.current = 0; // Reset on successful start
        } catch {}
      };

      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        try {
          lastResultAtRef.current = Date.now();
          // Reset consecutive failures on successful result
          consecutiveRestartFailuresRef.current = 0;
        } catch {}

        // Log that onresult was called
        console.log(
          "🔊 onresult called - results count:",
          event.results.length,
          "index:",
          event.resultIndex
        );

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          const isFinal = event.results[i].isFinal;

          // Log all speech input
          console.log(
            "🎤 User Speech Detected:",
            transcript,
            isFinal ? "(FINAL)" : "(INTERIM)"
          );
          if (isFinal) {
            finalTranscript += transcript;
            console.log("✅ Final Speech Result:", transcript);
          } else {
            interimTranscript += transcript;
          }
        }

        // Activate listening as soon as the specific phrase is heard (interim or final)
        const textToCheck = finalTranscript || interimTranscript;
        if (textToCheck) {
          const low = textToCheck.toLowerCase();
          const normalize = (s) =>
            s
              .toLowerCase()
              .replace(/[^\p{L}\p{N}\s]/gu, "") // strip punctuation
              .replace(/\s+/g, " ")
              .trim();
          const normLow = normalize(low);
          const wakePhrases = ["hi assistant"];

          if (awaitWakeWordRef.current) {
            // Allow help intent even before wake word for better onboarding
            try {
              const langCode = getLanguageCode(i18n.language);
              if (isHelpIntent(normLow, langCode)) {
                setShowCommandHelp(true);
                setFeedbackMessage("Showing available commands");
                if (voiceReplyEnabledRef.current) {
                  const now = Date.now();
                  if (now - (lastHelpTTSAtRef.current || 0) > 2000) {
                    lastHelpTTSAtRef.current = now;
                    speakWithPause(getVoicePhrase("helpIntro"));
                  }
                }
                // Do not proceed with wake detection for this utterance
                return;
              }
            } catch (e) {
              /* ignore */
            }
            const matched = wakePhrases.find((p) => {
              const np = normalize(p);
              return normLow.includes(np);
            });
            if (matched) {
              setAwaitWakeWord(false);
              // Cancel any pending auto-confirm timers
              if (commandProcessTimeoutRef.current) {
                clearTimeout(commandProcessTimeoutRef.current);
                commandProcessTimeoutRef.current = null;
              }
              // Strict flow: only activate; capture happens in subsequent utterances
              setTranscript("");
              setSpeechTranscript("");
              setCurrentStep("listening");
              setFeedbackMessage("Wake word detected — say your command");
              if (
                voiceReplyEnabledRef.current &&
                !wakePromptPlayedRef.current
              ) {
                speakReply(getVoicePhrase("whatHelp"));
                wakePromptPlayedRef.current = true;
              }
            } else {
              // Heuristic: allow obvious command-like phrases to proceed even without wake
              const langCode = getLanguageCode(i18n.language);
              const isCommandLike =
                /\b(go to|open|switch|select|mark|update|set|start|change|show)\b/.test(
                  normLow
                ) || isUndoIntent(normLow, langCode);
            }
            return; // ignore speech until wake phrase is spoken (unless command-like fallback handled)
          }
        }

        // Stream interim text once wake word has been detected
        if (!awaitWakeWordRef.current && interimTranscript) {
          setInterimTranscript(interimTranscript);
        }
        if (finalTranscript) {
          // Fallback: if first utterance is exactly the wake phrase, treat as wake detection
          // even if awaitWakeWordRef had a timing issue
          try {
            const normalize = (s) =>
              s
                .toLowerCase()
                .replace(/[^\p{L}\p{N}\s]/gu, "")
                .replace(/\s+/g, " ")
                .trim();
            const normFinal = normalize(finalTranscript);
            const exactWake =
              normalize("hi assistant") === normFinal ? "hi assistant" : null;
            if (
              exactWake &&
              (!speechTranscript || speechTranscript.trim() === "")
            ) {
              setAwaitWakeWord(false);
              setTranscript("");
              setSpeechTranscript("");
              setCurrentStep("listening");
              setFeedbackMessage("Wake word detected — say your command");
              if (
                voiceReplyEnabledRef.current &&
                !wakePromptPlayedRef.current
              ) {
                speakReply(getVoicePhrase("whatHelp"));
                wakePromptPlayedRef.current = true;
              }
              return; // don't accumulate transcript for pure wake phrase
            }
          } catch (e) {
            /* ignore normalize errors */
          }
          // Capture command speech once wake word has been detected
          // Clear interim transcript when a final chunk arrives
          setInterimTranscript("");
          setSpeechTranscript((prevTranscript) => {
            const newTranscript = prevTranscript
              ? `${prevTranscript} ${finalTranscript}`
              : finalTranscript;

            // Check for clean command - execute immediately without clicks
            // Only work when wake word is present in the CURRENT utterance (not accumulated)
            const langCode = getLanguageCode(i18n.language);

            // Help intent: show catalog of available commands for current page
            try {
              if (isHelpIntent(finalTranscript, langCode)) {
                setShowCommandHelp(true);
                setFeedbackMessage("Showing available commands");
                if (voiceReplyEnabledRef.current) {
                  const now = Date.now();
                  if (now - (lastHelpTTSAtRef.current || 0) > 2000) {
                    lastHelpTTSAtRef.current = now;
                    speakWithPause(getVoicePhrase("helpIntro"));
                  }
                }
                return prevTranscript; // stop further processing for help intent
              }
            } catch (e) {
              /* ignore help intent errors */
            }

            // Voice navigation: detect tab switching commands and dispatch navigation
            try {
              const normalizeNav = (s) =>
                s
                  .toLowerCase()
                  .replace(/[^\p{L}\p{N}\s]/gu, "")
                  .replace(/\s+/g, " ")
                  .trim();
              const textNav = normalizeNav(finalTranscript);
              // If a platform wake prefix is present (e.g., "INES, Platform: Events"),
              // treat subsequent words as a direct module selection without requiring action words.
              const platformPrefixed = hasPlatformWakePrefix(finalTranscript);
              // Multi-language action words for navigation
              const actionWords = [
                // English
                "go to",
                "open",
                "switch",
                "navigate",
                "show",
                // Spanish
                "ve a",
                "ir a",
                "ir a la",
                "ir al",
                "abrir",
                "cambiar",
                "cambiar a",
                "navegar",
                "navegar a",
                "mostrar",
                "mostrar la",
                "pestaña",
                "pestaña de",
                "pestaña de la",
              ];
              const hasAction = actionWords.some((a) => textNav.includes(a));
              const candidates = [
                {
                  key: "examDashboard",
                  phrases: [
                    "exam dashboard",
                    "exam-dashboard",
                    "tablero de examen",
                    "panel de examen",
                    "Abrir painel de exames",
                    "試験ダッシュボードを開く",
                    "Apri dashboard esami",
                    "परीक्षा डैशबोर्ड खोलें",
                    "Ouvrir le tableau de bord des examens",
                    "Abrir panel de exámenes",
                    "Open exam dashboard",
                    "Prüfungs-Dashboard öffnen",
                  ],
                },
                {
                  key: "attendance",
                  phrases: ["attendance", "asistencia", "asistencias"],
                },
                {
                  key: "assignments",
                  phrases: ["assignments", "assignment", "tareas", "tarea"],
                },
                {
                  key: "whiteboard",
                  phrases: [
                    "whiteboard",
                    "white board",
                    "white-board",
                    "pizarra",
                    "pizarra blanca",
                    "pizarra digital",
                  ],
                },
                {
                  key: "events",
                  phrases: ["events", "event", "eventos", "evento"],
                },
                { key: "booking", phrases: ["booking", "reserva", "reservas"] },
                { key: "history", phrases: ["history", "historial"] },
                {
                  key: "settings",
                  phrases: [
                    "settings",
                    "setting",
                    "configuración",
                    "configuraciones",
                  ],
                },
                {
                  key: "skillBridge",
                  phrases: [
                    "skill bridge",
                    "skillbridge",
                    "skill-bridge",
                    "puente de habilidades",
                  ],
                },
                {
                  key: "trusted-network",
                  phrases: [
                    "trusted network",
                    "trusted-network",
                    "red confiable",
                    "red de confianza",
                  ],
                },
                {
                  key: "dashboard",
                  phrases: ["dashboard", "tablero"],
                },
                {
                  key: "student",
                  phrases: ["student", "estudiante", "estudiantes"],
                },
              ];
              // Prefer the longest phrase match with word boundaries to avoid partial matches
              // Updated to handle multi-word phrases in multiple languages
              const scored = candidates
                .map((c) => {
                  const matchPhrase = c.phrases.find((p) => {
                    const np = normalizeNav(p);
                    // For multi-word phrases, use a more flexible regex that handles word boundaries
                    // Escape special regex characters in the phrase
                    const escaped = np.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                    // Match the phrase with word boundaries, but allow for spaces between words
                    const re = new RegExp(
                      `\\b${escaped.replace(/\s+/g, "\\s+")}\\b`,
                      "i"
                    );
                    return re.test(textNav);
                  });
                  return matchPhrase
                    ? {
                        key: c.key,
                        phrase: matchPhrase,
                        tokens: normalizeNav(matchPhrase).split(" ").length,
                        length: normalizeNav(matchPhrase).length,
                      }
                    : null;
                })
                .filter(Boolean)
                .sort((a, b) => b.tokens - a.tokens || b.length - a.length);
              const matched = scored[0];
              if (matched && (hasAction || platformPrefixed)) {
                const getRouteForTab = (key) => {
                  switch (key) {
                    case "attendance":
                      return "/attendance";
                    case "assignments":
                      return "/assignments";
                    case "dashboard":
                      return "/dashboard";
                    case "whiteboard":
                      return "/whiteboard";
                    case "events":
                      return user?.role === "Parent"
                        ? "/events/discover"
                        : "/events/my-events";
                    case "booking":
                      return "/booking";
                    case "history":
                      return "/history";
                    case "settings":
                      return "/settings";
                    case "skillBridge":
                      return "/skillBridge";
                    case "trusted-network":
                      return "/trusted-network";
                    case "examDashboard":
                      return "/examDashboard";
                    case "student":
                      return user?.role?.toLowerCase() === "parent"
                        ? "/parent/student"
                        : "/student";
                    default:
                      return "/attendance";
                  }
                };
                const path = getRouteForTab(matched.key);
                const auto = localStorage.getItem("acv_auto_confirm") === "1";
                const alreadyOnTarget = String(page || "").toLowerCase() === String(matched.key || "").toLowerCase();
                if (alreadyOnTarget) {
                  try {
                    localStorage.setItem("acv_open_full_on_arrival", "1");
                  } catch (e) {}
                  setIsVisible(true);
                  setCurrentStep("listening");
                  setFeedbackMessage("");
                  clearTranscript();
                  if (voiceReplyEnabledRef.current) {
                    speakWithPause(getVoicePhrase("alreadyOnTab", { tab: matched.key }));
                  }
                  return prevTranscript;
                }
                if (auto) {
                  window.postMessage(
                    { type: "VOICE_NAVIGATE", tabKey: matched.key, path },
                    "*"
                  );
                  try {
                    localStorage.setItem("acv_open_full_on_arrival", "1");
                  } catch (e) {}
                  setCurrentStep("processing");
                  setFeedbackMessage(`Navigating to ${matched.key}`);
                  setSpeechTranscript("");
                  setTranscript("");
                  if (voiceReplyEnabledRef.current) {
                    speakWithPause(getVoicePhrase("navigatingToTab", { tab: matched.key }));
                  }
                } else {
                  // In manual confirm mode, hold the command and show confirmation UI
                  setTranscript(newTranscript);
                  setPendingNav({ key: matched.key, path });
                  setCurrentStep("confirming");
                  setFeedbackMessage(`Navigate to ${matched.key}?`);
                }
                return prevTranscript; // stop further processing for navigation intent
              }
            } catch (e) {
              /* ignore navigation intent detection errors */
            }

            // Export Attendance: detect intent and click tagged export button
            try {
              if (isExportAttendanceIntent(finalTranscript)) {
                const auto = localStorage.getItem("acv_auto_confirm") === "1";
                let el = document.querySelector(
                  '[data-acv-tag="attendance-export"]'
                );

                // Manual confirm: keep listening and wait for explicit confirm (no modal)
                if (!auto) {
                  setTranscript(newTranscript);
                  setPendingNav({ key: "export-attendance" });
                  setCurrentStep("listening");
                  setFeedbackMessage(
                    "Say 'confirm' to export attendance, or 'cancel'."
                  );
                  return prevTranscript;
                }

                // Auto confirm ON: prepare dataset if needed then execute
                if (
                  !el ||
                  !(
                    Array.isArray(lastAttendanceDataset) &&
                    lastAttendanceDataset.length
                  )
                ) {
                  // Cancel any scheduled auto-processing to avoid double execution
                  if (commandProcessTimeoutRef.current) {
                    clearTimeout(commandProcessTimeoutRef.current);
                    commandProcessTimeoutRef.current = null;
                  }
                  // Reset export tracking and clear any pending error display
                  exportStartedRef.current = false;
                  if (exportErrorTimeoutRef.current) {
                    clearTimeout(exportErrorTimeoutRef.current);
                    exportErrorTimeoutRef.current = null;
                  }
                  const exportCmdText = newTranscript;
                  setCurrentStep("processing");
                  setFeedbackMessage("Preparing attendance export dataset...");
                  (async () => {
                    const fetched = await ensureGlobalExportDataset();
                    await new Promise((r) => setTimeout(r, 150));
                    const el2 = document.querySelector(
                      '[data-acv-tag="attendance-export"]'
                    );
                    const dataReady =
                      Array.isArray(lastAttendanceDataset) &&
                      lastAttendanceDataset.length;
                    if (!el2 || !(fetched || dataReady)) {
                      // Retry once after a short wait to allow dataset/render to settle
                      await new Promise((r) => setTimeout(r, 1000));
                      const el3 = document.querySelector(
                        '[data-acv-tag="attendance-export"]'
                      );
                      const dataReady2 =
                        Array.isArray(lastAttendanceDataset) &&
                        lastAttendanceDataset.length;
                      if (!el3 || !dataReady2) {
                        // Defer showing error; if export starts shortly after, suppress the error
                        exportErrorTimeoutRef.current = setTimeout(() => {
                          if (!exportStartedRef.current) {
                            setCurrentStep("error");
                            setFeedbackMessage(
                              "No records available to export. Try seeding attendance once."
                            );
                            // Log failed export intent
                            addToCommandLog(
                              {
                                type: "exportAttendance",
                                originalCommand: exportCmdText,
                              },
                              { message: "No records available to export." },
                              false
                            );
                          }
                        }, 800);
                        return;
                      }
                      try {
                        el3.click();
                        exportStartedRef.current = true;
                      } catch {}
                      if (exportErrorTimeoutRef.current) {
                        clearTimeout(exportErrorTimeoutRef.current);
                        exportErrorTimeoutRef.current = null;
                      }
                      setCurrentStep("success");
                      setFeedbackMessage("Attendance export started");
                      speakWithPause(getVoicePhrase("attendanceExportStarted"));
                      exportTTSGuardRef.current = false;
                      setSpeechTranscript("");
                      setTranscript("");
                      // Log successful export intent
                      addToCommandLog(
                        {
                          type: "exportAttendance",
                          originalCommand: exportCmdText,
                        },
                        { message: "Attendance export started" },
                        true
                      );
                      return;
                    }
                    try {
                      el2.click();
                      exportStartedRef.current = true;
                    } catch {}
                    if (exportErrorTimeoutRef.current) {
                      clearTimeout(exportErrorTimeoutRef.current);
                      exportErrorTimeoutRef.current = null;
                    }
                    setCurrentStep("processing");
                    setFeedbackMessage("Exporting attendance report");
                    setSpeechTranscript("");
                    setTranscript("");
                    setTimeout(() => {
                      setCurrentStep("success");
                      setFeedbackMessage("Attendance export started");
                      speakWithPause(getVoicePhrase("attendanceExportStarted"));
                      exportTTSGuardRef.current = false;
                      // Log successful export intent
                      addToCommandLog(
                        {
                          type: "exportAttendance",
                          originalCommand: exportCmdText,
                        },
                        { message: "Attendance export started" },
                        true
                      );
                    }, 250);
                  })();
                  return prevTranscript;
                }

                // Element exists: auto mode executes immediately
                const exportCmdText2 = newTranscript;
                try {
                  el.click();
                  exportStartedRef.current = true;
                } catch {}
                if (exportErrorTimeoutRef.current) {
                  clearTimeout(exportErrorTimeoutRef.current);
                  exportErrorTimeoutRef.current = null;
                }
                setCurrentStep("processing");
                setFeedbackMessage("Exporting attendance report");
                setSpeechTranscript("");
                setTranscript("");
                setTimeout(() => {
                  setCurrentStep("success");
                  setFeedbackMessage("Attendance export started");
                  speakWithPause(getVoicePhrase("attendanceExportStarted"));
                  exportTTSGuardRef.current = false;
                  // Log successful export intent
                  addToCommandLog(
                    {
                      type: "exportAttendance",
                      originalCommand: exportCmdText2,
                    },
                    { message: "Attendance export started" },
                    true
                  );
                }, 250);
                return prevTranscript;
              }
            } catch (e) {
              /* ignore export intent errors */
            }

            // Helper function to check if wake word is present in text
            const checkWakeWordPresent = (text) => {
              const normalize = (s) =>
                s
                  .toLowerCase()
                  .replace(/[^\p{L}\p{N}\s]/gu, "")
                  .replace(/\s+/g, " ")
                  .trim();
              const normText = normalize(text);
              const np = normalize("hi assistant");
              return normText.includes(np);
            };

            // Check clean command - must have wake word in CURRENT utterance only
            if (
              isCleanIntent(finalTranscript, langCode) &&
              checkWakeWordPresent(finalTranscript)
            ) {
              // Clear everything and continue listening
              setTranscript("");
              setSpeechTranscript("");
              setFeedbackMessage("Input cleared. Say your command.");
              setAwaitWakeWord(false);
              awaitWakeWordRef.current = false;
              setCurrentStep("listening");
              // Cancel any pending auto-confirm timers
              if (commandProcessTimeoutRef.current) {
                clearTimeout(commandProcessTimeoutRef.current);
                commandProcessTimeoutRef.current = null;
              }
              if (voiceReplyEnabledRef.current) {
                speakReply("Input cleared. Ready for your next command.");
              }
              return ""; // Return empty to clear the transcript
            }

            // Check for remote confirm command - execute pending command when auto-confirm is OFF
            // Only check if we have a previous transcript (pending command) and wake word is present in CURRENT utterance
            if (
              prevTranscript &&
              prevTranscript.trim() !== "" &&
              isConfirmIntent(finalTranscript, langCode) &&
              checkWakeWordPresent(finalTranscript)
            ) {
              // Check if auto-confirm is OFF
              if (localStorage.getItem("acv_auto_confirm") !== "1") {
                // Execute the pending command
                const pendingCommand = prevTranscript.trim();
                setFeedbackMessage("Executing command...");
                // Cancel any pending auto-confirm timers
                if (commandProcessTimeoutRef.current) {
                  clearTimeout(commandProcessTimeoutRef.current);
                  commandProcessTimeoutRef.current = null;
                }
                // Execute the pending command
                setTimeout(() => {
                  handleNoClick(pendingCommand);
                }, 100);
                return ""; // Clear the confirm command from transcript
              }
            }

            if (feedbackMessage) {
              setFeedbackMessage("");
            }
            // Auto-process after brief pause when autoConfirm enabled
            if (localStorage.getItem("acv_auto_confirm") === "1") {
              if (commandProcessTimeoutRef.current) {
                clearTimeout(commandProcessTimeoutRef.current);
              }
              commandProcessTimeoutRef.current = setTimeout(() => {
                setTranscript(newTranscript);
                // Trigger AI processing directly with explicit text;
                // let the handler decide the appropriate state
                handleNoClick(newTranscript);
              }, 1200);
            }
            return newTranscript;
          });
        }
      };

      recognition.onerror = (event) => {
        const dialogsOpen =
          showCommandLogRef.current || showCommandHelpRef.current;
        // Suppress noisy logs when we intentionally stop recognition
        if (event.error === "aborted" && explicitStopRef.current) {
          explicitStopRef.current = false;
          listeningRef.current = false;
          if (!dialogsOpen) setListening(false);
          return; // ignore and do not attempt restart
        }
        console.error("❌ Speech recognition error:", event.error);
        console.error("🚫 Error details:", {
          error: event.error,
          timeStamp: event.timeStamp,
          type: event.type,
          currentStep: currentStep,
          isVisible: isVisible,
          timestamp: new Date().toISOString(),
        });
        listeningRef.current = false;
        if (!dialogsOpen) setListening(false);

        // Only disable auto-restart for non-recoverable errors
        const nonRecoverable = [
          "not-allowed",
          "service-not-allowed",
          "audio-capture",
        ].includes(event.error);
        if (nonRecoverable) {
          shouldAutoRestartRef.current = false;
          // Avoid changing visible UI state while dialogs are open
          if (!dialogsOpen) {
            setCurrentStep("error");
            setFeedbackMessage(
              "Microphone permission or device issue detected. Please allow mic access and check input device."
            );
          }
          return;
        }

        // For recoverable errors (e.g., 'no-speech', 'network'), try to restart
        if (
          shouldAutoRestartRef.current &&
          isVisibleRef.current &&
          (currentStepRef.current === "listening" ||
            currentStepRef.current === "transcribing" ||
            currentStepRef.current === "idle")
        ) {
          setTimeout(() => {
            if (
              shouldAutoRestartRef.current &&
              isVisibleRef.current &&
              (currentStepRef.current === "listening" ||
                currentStepRef.current === "transcribing" ||
                currentStepRef.current === "idle") &&
              !listeningRef.current
            ) {
              try {
                console.log(
                  "🔄 onerror: Attempting to restart after recoverable error..."
                );
                recognition.start();
                consecutiveRestartFailuresRef.current = 0; // Reset on successful start
              } catch (err) {
                consecutiveRestartFailuresRef.current += 1;
                console.warn(
                  "⚠️ Failed to restart after error:",
                  err,
                  "Consecutive failures:",
                  consecutiveRestartFailuresRef.current
                );
                // Try again with longer delay
                setTimeout(() => {
                  if (
                    shouldAutoRestartRef.current &&
                    isVisibleRef.current &&
                    !listeningRef.current
                  ) {
                    try {
                      console.log("🔄 onerror: Retry attempt to restart...");
                      recognition.start();
                      consecutiveRestartFailuresRef.current = 0;
                    } catch (retryErr) {
                      consecutiveRestartFailuresRef.current += 1;
                      console.error(
                        "❌ Failed to restart after multiple attempts:",
                        retryErr,
                        "Consecutive failures:",
                        consecutiveRestartFailuresRef.current
                      );
                    }
                  }
                }, 3000);
              }
            }
          }, 800); // Shorter delay for faster recovery
        }
      };
      recognition.onend = () => {
        const dialogsOpen =
          showCommandLogRef.current || showCommandHelpRef.current;
        const now = Date.now();
        const timeSinceLastResult = now - (lastResultAtRef.current || 0);
        const timeSinceStart = now - (lastStartAtRef.current || 0);

        console.log(
          "🛑 onend fired - time since last result:",
          timeSinceLastResult,
          "ms, time since start:",
          timeSinceStart,
          "ms"
        );

        listeningRef.current = false;
        if (!dialogsOpen) setListening(false);

        // If we explicitly stopped, do not auto-restart and clear the flag
        if (explicitStopRef.current) {
          explicitStopRef.current = false;
          console.log("🛑 onend: Explicit stop detected, not restarting");
          return;
        }

        // Auto-restart listening if widget is still visible and we should be listening
        // Include more states where we want continuous listening
        const shouldRestart =
          shouldAutoRestartRef.current &&
          isVisibleRef.current &&
          !dialogsOpen &&
          (currentStepRef.current === "listening" ||
            currentStepRef.current === "transcribing" ||
            currentStepRef.current === "idle"); // Also restart from idle state

        if (shouldRestart) {
          console.log("🔄 onend: Attempting to restart recognition...");
          // Use a shorter timeout for more responsive listening
          setTimeout(() => {
            // Double-check state before restarting to prevent loops
            const canRestart =
              shouldAutoRestartRef.current &&
              isVisibleRef.current &&
              !dialogsOpen &&
              (currentStepRef.current === "listening" ||
                currentStepRef.current === "transcribing" ||
                currentStepRef.current === "idle") &&
              !listeningRef.current; // Only restart if not already listening

            if (canRestart) {
              try {
                console.log("🔄 onend: Starting recognition...");
                recognition.start();
                consecutiveRestartFailuresRef.current = 0; // Reset on successful start
              } catch (error) {
                consecutiveRestartFailuresRef.current += 1;
                console.warn(
                  "⚠️ Failed to auto-restart speech recognition:",
                  error,
                  "Consecutive failures:",
                  consecutiveRestartFailuresRef.current
                );

                // If we've failed multiple times, the recognition instance might be in a bad state
                // Try again after a longer delay
                setTimeout(() => {
                  if (
                    shouldAutoRestartRef.current &&
                    isVisibleRef.current &&
                    !dialogsOpen &&
                    !listeningRef.current
                  ) {
                    try {
                      console.log(
                        "🔄 onend: Retry attempt to start recognition..."
                      );
                      recognition.start();
                      consecutiveRestartFailuresRef.current = 0;
                    } catch (retryError) {
                      consecutiveRestartFailuresRef.current += 1;
                      console.error(
                        "❌ Failed to restart speech recognition after retry:",
                        retryError,
                        "Consecutive failures:",
                        consecutiveRestartFailuresRef.current
                      );

                      // If we've failed 3+ times, trigger a recreation of the recognition instance
                      if (consecutiveRestartFailuresRef.current >= 3) {
                        console.warn(
                          "⚠️ Multiple restart failures detected. Recognition may need recreation."
                        );
                        // The health check will handle recreation if needed
                      }
                    }
                  }
                }, 2000);
              }
            } else {
              console.log("🛑 onend: Cannot restart - conditions not met:", {
                shouldAutoRestart: shouldAutoRestartRef.current,
                isVisible: isVisibleRef.current,
                dialogsOpen,
                currentStep: currentStepRef.current,
                listening: listeningRef.current,
              });
            }
          }, 300); // Reduced delay for faster restart
        } else {
          console.log("🛑 onend: Not restarting - shouldRestart is false");
        }
      };

      // Store recognition in state for other functions to use
      setSpeechRecognition(recognition);
      setBrowserSupportsSpeechRecognition(true);

      // Cleanup function to prevent memory leaks and infinite loops
      return () => {
        if (recognition) {
          try {
            // Mark as intentional stop during cleanup
            explicitStopRef.current = true;
            shouldAutoRestartRef.current = false;
            stopHealthCheck(); // Stop health check during cleanup
            recognition.stop();
          } catch (error) {
            console.warn("Error stopping recognition during cleanup:", error);
          }
        }
        // Ensure audio meter interval is cleared to avoid memory leaks
        stopMeter();
      };
    } else {
      console.warn("Speech recognition is not supported by this browser");
      setBrowserSupportsSpeechRecognition(false);
    }
  }, [i18n.language]); // Only re-initialize when language changes

  // Switch recognition language dynamically: English for wake word, selected language for commands
  useEffect(() => {
    if (!speechRecognition) return;
    const desiredLang = awaitWakeWordRef.current
      ? "en-US"
      : getSpeechRecognitionLanguage(i18n.language || "en");
    if (speechRecognition.lang !== desiredLang) {
      try {
        speechRecognition.lang = desiredLang;
        if (listeningRef.current) {
          // Restart to ensure language change takes effect reliably
          try {
            explicitStopRef.current = true;
            speechRecognition.stop();
          } catch {}
          setTimeout(() => {
            try {
              explicitStopRef.current = false;
              speechRecognition.start();
            } catch {}
          }, 250);
        }
      } catch {}
    }
  }, [awaitWakeWord, i18n.language, speechRecognition]);

  // Set up speech recognition once
  useEffect(() => {
    if (speechRecognition) {
      setBrowserSupportsSpeechRecognition(true);
    }
  }, [speechRecognition]);

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      console.warn("Speech recognition is not supported by this browser");
      return;
    }
  }, [browserSupportsSpeechRecognition]);

  useEffect(() => {
    if (
      typeof speechTranscript === "string" &&
      speechTranscript.trim() !== ""
    ) {
      // When autoConfirm is off, keep confirm flow
      if (!autoConfirm) {
        setTranscript(speechTranscript);
        setCurrentStep("transcribing");
      }
    }
  }, [speechTranscript, autoConfirm]);

  // Removed: processVoiceCommand (AI is called only in handleNoClick)

  const startListening = (clearTranscript = false) => {
    if (browserSupportsSpeechRecognition && speechRecognition) {
      try {
        // Only start if not already listening to prevent loops
        if (listeningRef.current) {
          return;
        }
        // Always enable auto-restart when starting listening
        shouldAutoRestartRef.current = true;
        setCurrentStep("listening");
        setFeedbackMessage("");
        if (clearTranscript) {
          setTranscript("");
          setSpeechTranscript("");
          setInterimTranscript("");
          if (commandProcessTimeoutRef.current) {
            clearTimeout(commandProcessTimeoutRef.current);
            commandProcessTimeoutRef.current = null;
          }
        }

        try {
          console.log("🎤 startListening: Attempting to start recognition...");
          speechRecognition.start();
          consecutiveRestartFailuresRef.current = 0; // Reset on successful start
          // Start health check to ensure continuous listening
          startHealthCheck();
          // Start audio meter for waveform
          startMeter();
          console.log("✅ startListening: Recognition start initiated");
        } catch (startError) {
          consecutiveRestartFailuresRef.current += 1;
          console.error(
            "❌ Failed to start speech recognition:",
            startError,
            "Consecutive failures:",
            consecutiveRestartFailuresRef.current
          );
          // Try to restart after a short delay
          setTimeout(() => {
            if (shouldAutoRestartRef.current && isVisibleRef.current) {
              try {
                console.log(
                  "🔄 startListening: Retry attempt to start recognition..."
                );
                speechRecognition.start();
                consecutiveRestartFailuresRef.current = 0;
                startHealthCheck();
                startMeter();
              } catch (retryError) {
                consecutiveRestartFailuresRef.current += 1;
                console.error(
                  "❌ Failed to restart speech recognition:",
                  retryError,
                  "Consecutive failures:",
                  consecutiveRestartFailuresRef.current
                );
                setCurrentStep("error");
                setFeedbackMessage(
                  "Failed to start voice recognition. Please try again."
                );
              }
            }
          }, 1000);
        }
      } catch (error) {
        console.error("❌ Failed to start speech recognition:", error);
        setCurrentStep("idle");
      }
    } else {
      console.warn(
        "⚠️ Cannot start listening - Speech recognition not available"
      );
    }
  };

  const stopListening = () => {
    if (speechRecognition) {
      try {
        // Disable auto-restart when explicitly stopped
        // Mark as an intentional stop
        explicitStopRef.current = true;
        shouldAutoRestartRef.current = false;
        stopHealthCheck(); // Stop health check when explicitly stopping
        speechRecognition.stop();
        // Stop audio meter
        stopMeter();

        // Clear any pending auto-confirm processing timers
        if (commandProcessTimeoutRef.current) {
          clearTimeout(commandProcessTimeoutRef.current);
          commandProcessTimeoutRef.current = null;
        }

        // Set to paused state when stopping
        if (["listening", "transcribing"].includes(currentStep)) {
          setCurrentStep("paused");
        }
      } catch (error) {
        console.error("❌ Failed to stop speech recognition:", error);
      }
    } else {
      console.warn(
        "⚠️ Cannot stop listening - Speech recognition not available"
      );
    }
  };

  // Stop recognition for processing without switching UI to paused
  const suspendRecognitionForProcessing = () => {
    try {
      if (speechRecognition && listeningRef.current) {
        explicitStopRef.current = true;
        shouldAutoRestartRef.current = false;
        stopHealthCheck();
        speechRecognition.stop();
      }
    } catch (e) {
      console.warn("[Voice] Failed to suspend recognition:", e?.message);
    }
    stopMeter();
  };

  const resumeListeningIfOpen = (clearTranscript = false) => {
    if (!isVisibleRef.current) return;
    startListening(clearTranscript);
  };

  const handleConfirmClick = () => {
    // Confirm should open Yes/No confirmation UI
    setCurrentStep("confirming");
  };

  const handleYesClick = () => {
    // Yes means re-speak - Keep the old transcript but start listening again
    // Don't clear the transcript so the old text remains visible
    setCurrentStep("listening");
    // Don't reset the transcript to keep the old text
    // Don't pass true to startListening to avoid clearing the transcript
    startListening(false);
  };

  const handleNoClick = async (cmdText) => {
    // No means proceed with command - Execute the voice command directly
    // If React onClick passed a MouseEvent, ignore it and use current transcript
    let providedText = cmdText;
    const isEventObject =
      providedText &&
      typeof providedText === "object" &&
      ("nativeEvent" in providedText || "target" in providedText);
    if (isEventObject) {
      try {
        providedText.preventDefault && providedText.preventDefault();
        providedText.stopPropagation && providedText.stopPropagation();
      } catch {}
      providedText = null;
    }

    // Prevent duplicate auto-confirm timers firing while we process
    if (commandProcessTimeoutRef.current) {
      clearTimeout(commandProcessTimeoutRef.current);
      commandProcessTimeoutRef.current = null;
    }

    const raw = String(
      (typeof providedText === "string" ? providedText : transcript) || ""
    ).trim();
    const lowerRaw = raw.toLowerCase();
    const normalize = (s) =>
      s
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, "")
        .replace(/\s+/g, " ")
        .trim();
    const normLowerRaw = normalize(lowerRaw);
    const basicWakeOnly = ["hi assistant"];
    if (!raw || basicWakeOnly.some((p) => normLowerRaw === normalize(p))) {
      setIsNoProcessing(false);
      setCurrentStep("listening");
      setFeedbackMessage("Say a command after the wake word.");
      return;
    }

    if (!onVoiceCommand) {
      console.error("❌ No onVoiceCommand handler provided");
      return;
    }

    setIsNoProcessing(true);

    // If there is a pending navigation from manual confirm, execute it directly
    if (pendingNav && pendingNav.path) {
      try {
        window.postMessage(
          {
            type: "VOICE_NAVIGATE",
            tabKey: pendingNav.key,
            path: pendingNav.path,
          },
          "*"
        );
      } catch (e) {
        console.warn("Failed to post VOICE_NAVIGATE", e);
      }
      try {
        localStorage.setItem("acv_open_full_on_arrival", "1");
      } catch (e) {}
      if (voiceReplyEnabledRef.current) {
        speakWithPause(
          getVoicePhrase("navigatingToTab", { tab: pendingNav.key })
        );
      }
      if (pendingNav.after === "export-attendance") {
        try {
          localStorage.setItem("acv_export_after_nav", "1");
        } catch {}
      }
      setCurrentStep("processing");
      setFeedbackMessage(`Navigating to ${pendingNav.key}`);
      setSpeechTranscript("");
      setTranscript("");
      setPendingNav(null);
      setIsNoProcessing(false);
      return;
    }

    // Execute pending export when manual confirm is ON
    if (pendingNav && pendingNav.key === "export-attendance") {
      const exportCmdText = String(transcript || "").trim();
      let el = document.querySelector('[data-acv-tag="attendance-export"]');
      // If exporter is not present or dataset missing, fetch before executing
      if (
        !el ||
        !(Array.isArray(lastAttendanceDataset) && lastAttendanceDataset.length)
      ) {
        try {
          const fetched = await ensureGlobalExportDataset();
          await new Promise((r) => setTimeout(r, 150));
          el = document.querySelector('[data-acv-tag="attendance-export"]');
          if (!fetched || !el) {
            // Defer showing error, in case export becomes ready shortly after
            exportErrorTimeoutRef.current = setTimeout(() => {
              if (!exportStartedRef.current) {
                setFeedbackMessage(
                  "No records available to export. Try seeding attendance once."
                );
                setCurrentStep("error");
                if (voiceReplyEnabledRef.current) {
                  speakReply("No records available to export");
                }
                // Log failed export intent
                addToCommandLog(
                  { type: "exportAttendance", originalCommand: exportCmdText },
                  { message: "No records available to export." },
                  false
                );
              }
            }, 800);
            setPendingNav(null);
            setIsNoProcessing(false);
            return;
          }
        } catch {
          exportErrorTimeoutRef.current = setTimeout(() => {
            if (!exportStartedRef.current) {
              setFeedbackMessage("Failed to prepare export dataset");
              setCurrentStep("error");
              speakWithPause(getVoicePhrase("exportFailed"));
              // Log failed export intent
              addToCommandLog(
                { type: "exportAttendance", originalCommand: exportCmdText },
                { message: "Failed to prepare export dataset" },
                false
              );
            }
          }, 800);
          setPendingNav(null);
          setIsNoProcessing(false);
          return;
        }
      }

      try {
        el.click();
        exportStartedRef.current = true;
      } catch {}
      if (exportErrorTimeoutRef.current) {
        clearTimeout(exportErrorTimeoutRef.current);
        exportErrorTimeoutRef.current = null;
      }
      setCurrentStep("processing");
      setFeedbackMessage("Exporting attendance report");
      setSpeechTranscript("");
      setTranscript("");
      setPendingNav(null);
      setIsNoProcessing(false);
      setTimeout(() => {
        setCurrentStep("success");
        setFeedbackMessage("Attendance export started");
        speakWithPause(getVoicePhrase("attendanceExportStarted"));
        exportTTSGuardRef.current = false;
        // Log successful export intent
        addToCommandLog(
          { type: "exportAttendance", originalCommand: exportCmdText },
          { message: "Attendance export started" },
          true
        );
      }, 250);
      return;
    }

    try {
      // Use basic AI parsing for direct execution
      // Lightweight local parsing for special commands (undo, bring element by code)
      const lower = lowerRaw;
      let aiParsed = null;

      // Undo intent per selected language
      const langCode = getLanguageCode(i18n.language);
      if (isUndoIntent(lowerRaw, langCode)) {
        aiParsed = { type: "undo", originalCommand: raw };
      }

      // Redo intent - language-aware synonyms
      if (!aiParsed) {
        if (isRedoIntent(lowerRaw, langCode)) {
          aiParsed = { type: "redo", originalCommand: raw };
        }
      }

      // Clean intent - clear current input and continue listening
      if (!aiParsed) {
        if (isCleanIntent(lowerRaw, langCode)) {
          // Clear transcript and reset to listening state
          setTranscript("");
          setSpeechTranscript("");
          setFeedbackMessage("Input cleared. Say your command.");
          setAwaitWakeWord(false);
          awaitWakeWordRef.current = false;
          setCurrentStep("listening");
          setIsNoProcessing(false);
          return; // Don't process further, just clear and continue
        }
      }

      // Library element by code: matches E1234 or similar
      if (!aiParsed) {
        // Try to capture codes like E1487, e1487, or any letter+digits
        const codeMatch = raw.match(/\b([A-Za-z][0-9]{3,})\b/);
        const hasBringVerb =
          /\b(bring|cargar|carga|load|fetch)\b/i.test(lower) ||
          /\belement(o)?\b/i.test(lower);
        if (codeMatch && hasBringVerb) {
          aiParsed = {
            type: "bringElement",
            elementCode: codeMatch[1].toUpperCase(),
            originalCommand: raw,
          };
        }
      }

      // Local navigation parsing: "go to/open/switch to the lesson <name>"
      if (!aiParsed) {
        const textNoPolite = lower.replace(/^(please|kindly)\s+/i, "").trim();
        const goToLessonMatch = textNoPolite.match(
          /\b(go to|open|switch to)\s+(?:the\s+)?lesson\s+(.+)/i
        );
        if (goToLessonMatch) {
          aiParsed = {
            type: "goToLesson",
            lessonName: goToLessonMatch[2]?.trim(),
            originalCommand: raw,
          };
        }
      }

      // Local next/previous lesson parsing
      if (!aiParsed) {
        if (/\b(next)\s+(lesson)\b/i.test(lower)) {
          aiParsed = { type: "nextLesson", originalCommand: raw };
        } else if (/\b(previous|prev)\s+(lesson)\b/i.test(lower)) {
          aiParsed = { type: "previousLesson", originalCommand: raw };
        }
      }

      // Fall back to AI parser for other commands
      if (!aiParsed) {
        const slug = getSlugParam();
        aiParsed = await parseAttendanceCommandWithAI(raw, studentList, slug);
      }

      if (!aiParsed || !aiParsed.type) {
        // For whiteboard or drawing-like commands, fall back to sending the raw transcript
        // so the Whiteboard page can parse drawing intents locally.
        const looksLikeWhiteboardCmd = (() => {
          try {
            const txt = String(raw || "").toLowerCase();
            const drawWords = /(draw|sketch|create|make|dibuja|dibujar|desenhar|dessine|zeichnen|disegna|描いて|描く|बनाओ|बनाएं)/.test(txt);
            const shapeWords = /(rectangle|rect(angulo|ángulo)?|cuadrado|retângulo|quadrato|rechteck|circle|ellipse|círculo|circulo|elipse|cercle|kreis|cerchio|ellisse|arrow|flecha|pfeil|freccia|line|línea|linea|linha|text|texto|texte|testo|formula|equation|fórmula|ecuación|équation|gleichung|latex)/.test(txt);
            const libraryWords = /(library|biblioteca|colecciones|collections|collection)/.test(txt);
            return drawWords || shapeWords || libraryWords || page === "whiteboard";
          } catch (_) {
            return page === "whiteboard";
          }
        })();

        if (looksLikeWhiteboardCmd) {
          // Show processing state in the widget while whiteboard handles the command
          setCurrentStep("processing");
          // If user requested a lesson plan, show a clear progress message
          try {
            const txt = String(raw || "").toLowerCase();
            const isLessonPlanReq =
              /\blesson\s+plan\b/.test(txt) ||
              /\b(create|generate|make|build|prepare|plan)\b.*\blesson\b/.test(
                txt
              ) ||
              /\blesson\s+(with|about|on|for)\s+/.test(txt);
            const isChartReq =
              /\b(diagram|chart|graph)\b/.test(txt) ||
              /\b(create|generate|make|build|prepare|draw)\b.*\b(diagram|chart|graph)\b/.test(
                txt
              ) ||
              /\b(flowchart|timeline|pie\s+chart|bar\s+chart|line\s+chart)\b/.test(
                txt
              );
            const isSpinWheelReq =
              /\b(spin\s*wheel|spinner|spinning\s*wheel|spinwheel)\b/.test(txt);
            if (isLessonPlanReq) {
              setFeedbackMessage("Generating lesson plan…");
            } else if (isChartReq) {
              setFeedbackMessage("Generating chart…");
            } else if (isSpinWheelReq) {
              setFeedbackMessage("Generating spin wheel…");
              setCurrentStep("processing");
              setIsSpinWheelOpActive(true);
            }
          } catch (e) {
            /* ignore */
          }

          const result = await onVoiceCommand({
            type: null,
            originalCommand: raw,
            page,
          });
          setIsNoProcessing(false);
          if (result && result.success) {
            lastSuccessAtRef.current = Date.now();
            const msg = result.message || "Done";
            const isSpinMsg =
              /Generating spin wheel/i.test(msg) ||
              /Opened Spin Wheel/i.test(msg);
            if (
              page === "whiteboard" &&
              (isSpinMsg || isSpinWheelOpActiveRef.current)
            ) {
              setCurrentStep("processing");
              setFeedbackMessage("Generating spin wheel…");
              setIsSpinWheelOpActive(true);
            } else {
              setCurrentStep("success");
              setFeedbackMessage(msg);
              try {
                if (voiceReplyEnabledRef.current) {
                  const reply = buildVoiceReply({ originalCommand: raw, type: null }, result);
                  speakReply(reply);
                }
              } catch (_) {}
            }
            if (localStorage.getItem("acv_auto_confirm") !== "1") {
              if (!isSpinWheelOpActiveRef.current) {
                setTimeout(() => {
                  setIsVisible(false);
                  resetWidget();
                }, 3000);
              }
            } else {
              // In auto-confirm mode, keep listening and clear transcript for next command
              if (!isSpinWheelOpActiveRef.current) {
                setTimeout(() => {
                  clearTranscript();
                  setFeedbackMessage("");
                  setIsNoProcessing(false);
                  setAwaitWakeWord(false);
                  awaitWakeWordRef.current = false;
                  setCurrentStep("listening");
                }, 800);
              }
            }
          } else {
            const recentSuccess =
              Date.now() - (lastSuccessAtRef.current || 0) < 2000;
            if (recentSuccess) {
              // Suppress transient error after a recent success (e.g., partial follow-up transcript)
              setIsNoProcessing(false);
              setFeedbackMessage("");
              setAwaitWakeWord(false);
              awaitWakeWordRef.current = false;
              setCurrentStep("listening");
              if (localStorage.getItem("acv_auto_confirm") === "1") {
                setTimeout(() => {
                  clearTranscript();
                }, 400);
              }
              return;
            }
            setCurrentStep("error");
            setFeedbackMessage(
              result?.message || "Command not recognized. Please try again."
            );
            if (localStorage.getItem("acv_auto_confirm") !== "1") {
              setTimeout(() => {
                setIsVisible(false);
                resetWidget();
              }, 5000);
            } else {
              setTimeout(() => {
                clearTranscript();
                setIsNoProcessing(false);
                setAwaitWakeWord(false);
                awaitWakeWordRef.current = false;
                setCurrentStep("listening");
              }, 1200);
            }
          }
          return;
        }

        console.error("❌ AI parsing failed - Command not recognized");
        setIsNoProcessing(false);
        setCurrentStep("error");
        setFeedbackMessage("Command not recognized. Please try again.");

        const suggestions = studentList
          .slice(0, 5)
          .map((s) => `Try: "Mark ${s.StudentName} present"`);

        if (localStorage.getItem("acv_auto_confirm") !== "1") {
          setTimeout(() => {
            setIsVisible(false);
            resetWidget();
          }, 5000);
        } else {
          setTimeout(() => {
            clearTranscript();
            setIsNoProcessing(false);
            setAwaitWakeWord(false);
            awaitWakeWordRef.current = false;
            setCurrentStep("listening");
          }, 1200);
        }
        return;
      }
      await executeCommand(aiParsed);
    } catch (error) {
      console.error("❌ Command processing error:", error);
      setIsNoProcessing(false);
      setCurrentStep("error");
      setFeedbackMessage("Error processing command. Please try again.");

      if (localStorage.getItem("acv_auto_confirm") !== "1") {
        setTimeout(() => {
          setIsVisible(false);
          resetWidget();
        }, 5000);
      } else {
        setTimeout(() => {
          clearTranscript();
          setIsNoProcessing(false);
          setAwaitWakeWord(false);
          awaitWakeWordRef.current = false;
          setCurrentStep("listening");
        }, 1200);
      }
    }
  };

  // Top-level helper: persist undo fallback for single attendance/note actions
  // Note: similar helper exists inside a recognition setup block; this one is accessible to executeCommand
  const persistUndoFallback = (result) => {
    try {
      if (!result || !result.type) return false;
      const type = result.type;
      const isAttendance = ["present", "absent", "late"].includes(type);
      const isNote = ["addNote", "updateNote", "clearNote"].includes(type);
      if (!isAttendance && !isNote) return false;

      const target =
        result.matchedStudent ||
        studentList.find(
          (s) =>
            s.StudentName &&
            result.studentName &&
            s.StudentName.toLowerCase() === result.studentName.toLowerCase()
        ) ||
        null;
      if (!target) return false;

      const email = target.StudentEmailId;
      const lessonId = target.LessonId;
      let entry = null;

      if (isAttendance) {
        let nextAttendance = "Presente";
        if (type === "absent") {
          nextAttendance =
            result.reason &&
            typeof result.reason === "string" &&
            result.reason.trim()
              ? result.reason
              : "Ausente";
        }
        if (type === "late") nextAttendance = "Retardo";
        entry = {
          timestamp: Date.now(),
          type: "attendance",
          email,
          lessonId,
          previous: target.Attendance ?? null,
          next: nextAttendance,
        };
      } else if (isNote) {
        const nextNote = result.noteText ?? "";
        entry = {
          timestamp: Date.now(),
          type: "note",
          email,
          lessonId,
          previous: target.Notes ?? "",
          next: nextNote,
        };
      }

      if (!entry) return false;
      sessionStorage.setItem(
        "ACV_ATTENDANCE_LAST_ACTION",
        JSON.stringify(entry)
      );
      return true;
    } catch (e) {
      return false;
    }
  };

  const executeCommand = async (parsedResult) => {
    setIsNoProcessing(true);
    // Suspend mic while executing to avoid capturing background speech
    suspendRecognitionForProcessing();

    try {
      // Set fallback snapshot before executing, if applicable
      const fallbackSet = persistUndoFallback(parsedResult);
      const commandPayload = {
        type: parsedResult.type,
        studentName: parsedResult.studentName,
        reason: parsedResult.reason,
        originalCommand: parsedResult.originalCommand,
        page: page,
        matchedStudent: parsedResult.matchedStudent,
        confidence: parsedResult.confidence,
        multipleStudents: parsedResult.multipleStudents,
        multipleCommands: parsedResult.multipleCommands, // Add support for multiple commands
        lessonName: parsedResult.lessonName,
        noteText: parsedResult.noteText, // Add support for note text
        elementCode: parsedResult.elementCode || null, // Library element code support
      };

      // Enter processing state and show context-aware feedback
      setCurrentStep("processing");
      // If this is a whiteboard lesson plan or chart request, inform the user immediately
      try {
        const ocTxt = String(parsedResult?.originalCommand || "").toLowerCase();
        const isLessonPlanReq =
          page === "whiteboard" &&
          (/(^|\b)lesson\s+plan\b/.test(ocTxt) ||
            /\b(create|generate|make|build|prepare|plan)\b.*\blesson\b/.test(
              ocTxt
            ) ||
            /\blesson\s+(with|about|on|for)\s+/.test(ocTxt));
        const isChartReq =
          page === "whiteboard" &&
          (/\b(diagram|chart|graph)\b/.test(ocTxt) ||
            /\b(create|generate|make|build|prepare|draw)\b.*\b(diagram|chart|graph)\b/.test(
              ocTxt
            ) ||
            /\b(flowchart|timeline|pie\s+chart|bar\s+chart|line\s+chart)\b/.test(
              ocTxt
            ));
        if (isLessonPlanReq) {
          setFeedbackMessage("Generating lesson plan…");
        } else if (isChartReq) {
          setFeedbackMessage("Generating chart…");
        }
      } catch (e) {
        /* ignore */
      }

      const result = await onVoiceCommand(commandPayload);

      if (result && result.success) {
        lastSuccessAtRef.current = Date.now();
        setIsNoProcessing(false);
        setCurrentStep("success");
        setFeedbackMessage(
          result.message || "Attendance updated successfully!"
        );

        // Store last executed voice command for diagnostics
        try {
          sessionStorage.setItem(
            "ACV_VOICE_LAST_CMD",
            JSON.stringify(parsedResult)
          );
        } catch (e) {}

        // Add to command log
        addToCommandLog(parsedResult, result, true);

        if (voiceReplyEnabledRef.current) {
          const reply = buildVoiceReply(parsedResult, result);
          speakReply(reply);
        }

        if (localStorage.getItem("acv_auto_confirm") !== "1") {
          setTimeout(() => {
            setIsVisible(false);
            resetWidget();
          }, 3000);
        } else {
          // Keep listening and clear transcript for next command in auto-confirm mode
          setTimeout(() => {
            clearTranscript();
            setFeedbackMessage("");
            setAwaitWakeWord(false);
            awaitWakeWordRef.current = false;
            setCurrentStep("listening");
            resumeListeningIfOpen(false);
          }, 800);
        }
      } else {
        console.error(`❌ Command execution failed:`, result);
        // If we set a fallback but the command fnailed, clear the fallback to avoid stale undo
        if (fallbackSet) {
          try {
            sessionStorage.removeItem("ACV_ATTENDANCE_LAST_ACTION");
          } catch (e) {
            console.error(
              "[UNDO-WIDGET] Failed to clear fallback:",
              e?.message
            );
          }
        }

        // Add failed command to log
        addToCommandLog(parsedResult, result, false);
        setIsNoProcessing(false);
        setCurrentStep("error");
        setFeedbackMessage(
          result?.message || "Command execution failed. Please try again."
        );

        if (localStorage.getItem("acv_auto_confirm") !== "1") {
          setTimeout(() => {
            setIsVisible(false);
            resetWidget();
          }, 5000);
        } else {
          setTimeout(() => {
            clearTranscript();
            setAwaitWakeWord(false);
            awaitWakeWordRef.current = false;
            setCurrentStep("listening");
            resumeListeningIfOpen(false);
          }, 1200);
        }
      }
    } catch (error) {
      console.error(`❌ Error executing command:`, error);
      setIsNoProcessing(false);
      setCurrentStep("error");
      setFeedbackMessage("Error executing command. Please try again.");

      if (localStorage.getItem("acv_auto_confirm") !== "1") {
        setTimeout(() => {
          setIsVisible(false);
          resetWidget();
        }, 5000);
      } else {
        setTimeout(() => {
          clearTranscript();
          setAwaitWakeWord(false);
          awaitWakeWordRef.current = false;
          setCurrentStep("listening");
          resumeListeningIfOpen(false);
        }, 1200);
      }
    }
  };

  const clearTranscript = () => {
    setTranscript("");
    setSpeechTranscript("");
    setInterimTranscript("");
  };

  const resetWidget = () => {
    setCurrentStep("idle");
    setTranscript("");
    setFeedbackMessage("");
    setSpeechTranscript("");
    setInterimTranscript("");
    shouldAutoRestartRef.current = false;
    stopHealthCheck(); // Stop health check when resetting widget
    stopListening();
  };

  const handleCloseWidget = () => {
    resetWidget();
    setIsVisible(false);
  };

  const handleResumeListening = () => {
    setAwaitWakeWord(true);
    wakePromptPlayedRef.current = false;
    startListening(false);
  };

  const speakWithPause = (text) => {
    if (!voiceReplyEnabledRef.current) return;
    const now = Date.now();
    if (now - (lastTTSAtRef.current || 0) < 400) return;
    lastTTSAtRef.current = now;
    stopListening();
    speakReply(text, {
      onend: () => {
        resumeListeningIfOpen(false);
      },
    });
  };

  // Remove wake words from transcript display so only the user command shows
  const toggleWidget = () => {
    if (!isVisible) {
      // Calculate modal dimensions based on compact mode
      const modalWidth = compactMode ? 320 : 320;
      const modalHeight = compactMode ? 180 : 280;

      // Position the modal to ensure it's fully visible on screen
      let newX = micPosition.x - modalWidth / 2; // Center horizontally relative to mic button
      let newY = micPosition.y - modalHeight - 20; // Position above the mic button with gap

      // Special handling for bottom-right corner microphone button
      if (
        micPosition.x > window.innerWidth - 100 &&
        micPosition.y > window.innerHeight - 100
      ) {
        // If mic is in bottom-right corner, position modal to the left and above
        newX = window.innerWidth - modalWidth - 40;
        newY = window.innerHeight - modalHeight - 210;
      }

      // Ensure modal doesn't go off-screen
      newX = Math.max(10, Math.min(newX, window.innerWidth - modalWidth - 10));
      newY = Math.max(
        10,
        Math.min(newY, window.innerHeight - modalHeight - 10)
      );

      // If the calculated position would still cut off the modal, use a safe fallback position
      if (newY + modalHeight > window.innerHeight - 10) {
        newY = window.innerHeight - modalHeight - 50; // Position from bottom with margin
      }
      if (newX + modalWidth > window.innerWidth - 10) {
        newX = window.innerWidth - modalWidth - 20; // Position from right with margin
      }

      const newPosition = { x: newX, y: newY };

      // First set the current step to idle to prevent auto-restart loop
      setCurrentStep("idle");
      setPosition(newPosition);
      // Then make the widget visible
      setIsVisible(true);

      // Start listening after a short delay, but only if widget is still visible
      setTimeout(() => {
        if (!isVisibleRef.current) return;
        if (browserSupportsSpeechRecognition) {
          setCurrentStep("listening"); // UI shows listening
          setAwaitWakeWord(true); // enable wake word stage
          awaitWakeWordRef.current = true; // update ref immediately
          wakePromptPlayedRef.current = false;
          startListening(true); // Clear transcript when opening widget
        } else {
          setCurrentStep("error");
          setFeedbackMessage(
            "Voice recognition is not supported in this browser. Try Chrome or Edge, or enable microphone permissions."
          );
        }
      }, 300); // Increased delay to ensure state updates properly
    } else {
      resetWidget();
      setIsVisible(false);
    }
  };

  // Drag functionality for widget
  const handleMouseDown = (e) => {
    if (e.target.closest(".cue-content")) return; // Don't drag when clicking on content

    setIsDragging(true);
    const rect = widgetRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  // Handle microphone button click (no dragging)
  const handleMicClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWidget();
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStartRef.current.x;
      const newY = e.clientY - dragStartRef.current.y;

      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - 320, newX)),
        y: Math.max(0, Math.min(window.innerHeight - 280, newY)),
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  // Handle window resize
  useEffect(() => {
    const handleWindowResize = () => {
      const newWindowWidth = window.innerWidth;
      const newWindowHeight = window.innerHeight;
      const buttonSize = 60;
      const margin = 20;

      // Always reposition microphone button to bottom-right corner
      const newMicPosition = {
        x: Math.max(margin, newWindowWidth - buttonSize - margin),
        y: Math.max(margin, newWindowHeight - buttonSize - margin),
      };
      setMicPosition(newMicPosition);

      // Update modal position if it's visible
      if (isVisible) {
        const modalWidth = compactMode ? 320 : 320;
        const modalHeight = compactMode ? 180 : 280;

        setPosition((prev) => {
          let newX = prev.x;
          let newY = prev.y;

          // Ensure modal stays within screen bounds
          newX = Math.min(newX, newWindowWidth - modalWidth - margin);
          newY = Math.min(newY, newWindowHeight - modalHeight - margin);

          // If modal would go off-screen, reposition it
          if (newX < margin) newX = margin;
          if (newY < margin) newY = margin;

          return { x: newX, y: newY };
        });
      }
    };

    // Add immediate resize handler for better responsiveness
    window.addEventListener("resize", handleWindowResize);

    // Also add a throttled version for performance during rapid resizing
    let resizeTimeout;
    const throttledResizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleWindowResize, 50);
    };
    window.addEventListener("resize", throttledResizeHandler);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("resize", throttledResizeHandler);
      clearTimeout(resizeTimeout);
    };
  }, [isVisible]);

  // Friendly labels/messages for log entries
  const getFriendlyType = (type = "") => {
    const t = String(type);
    // Broad media mappings
    if (/^insert.*image/i.test(t)) return "Insert image";
    if (/^insert.*audio/i.test(t)) return "Insert audio";
    if (/^insert.*video/i.test(t)) return "Insert video";
    if (/^open.*library/i.test(t)) return "Open library";

    switch (t) {
      case "cancelActiveDialog":
        return "Close dialog";
      case "primeVoiceCache":
        return "Voice assistant ready";
      case "insertVideoByQuery":
        return "Insert video";
      case "openLibrary":
        return "Open library";
      case "present":
        return "Mark present";
      case "absent":
        return "Mark absent";
      case "late":
        return "Mark late";
      case "goToLesson":
        return "Go to lesson";
      case "nextLesson":
        return "Next lesson";
      case "previousLesson":
        return "Previous lesson";
      case "navigateLesson":
        return "Navigate lesson";
      case "addNote":
        return "Add note";
      case "updateNote":
        return "Update note";
      case "clearNote":
        return "Clear note";
      case "drawShape":
        return "Draw shape";
      case "freedraw":
        return "Free draw";
      case "drawFrame":
        return "Draw frame";
      default:
        return type || "Command";
    }
  };

  const getFriendlyMessage = (type, rawMessage, success) => {
    const msg = String(rawMessage || "").trim();
    const t = String(type);
    // Broad media messages
    if (/^insert.*image/i.test(t))
      return success ? "Image inserted" : msg || "Image insert failed";
    if (/^insert.*audio/i.test(t))
      return success ? "Audio inserted" : msg || "Audio insert failed";
    if (/^insert.*video/i.test(t))
      return success ? "Video inserted" : msg || "Video insert failed";
    if (/^open.*library/i.test(t))
      return success ? "Library opened" : msg || "Library open failed";

    switch (t) {
      case "cancelActiveDialog":
        if (!success) {
          // Normalize technical message
          if (/Cancel button not found/i.test(msg)) {
            return "Dialog could not be closed: no close button available.";
          }
          return msg || "Dialog could not be closed.";
        }
        return "Dialog closed";
      case "primeVoiceCache":
        return success
          ? "Voice assistant prepared for this page"
          : msg || "Voice assistant preparation failed";
      case "insertVideoByQuery":
        return success ? "Video inserted" : msg || "Video insert failed";
      case "openLibrary":
        return success ? "Library opened" : msg || "Library open failed";
      default:
        return msg || (success ? "Command completed" : "Command failed");
    }
  };

  // Command Log Functions
  const addToCommandLog = async (parsedResult, execResult, success) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      command: parsedResult.originalCommand || "",
      type: parsedResult.type || "unknown",
      displayType: getFriendlyType(parsedResult.type || "unknown"),
      studentName: parsedResult.studentName || "",
      success: success,
      message: getFriendlyMessage(
        parsedResult.type || "unknown",
        execResult?.message,
        success
      ),
      page: page,
    };

    setCommandLog((prevLog) => {
      const newLog = [logEntry, ...prevLog].slice(0, 100); // Keep only last 100 entries
      return newLog;
    });

    // Best-effort persistence to server; do not block UI
    await persistCommandLogToServer(logEntry);
  };

  // Listen for whiteboard reply messages and log them (whiteboard page only)
  const recentReplyRef = useRef({ action: null, t: 0 });
  useEffect(() => {
    const handler = (e) => {
      try {
        const d = e?.data;
        if (!d || typeof d !== "object") return;
        if (d.type !== "acv.whiteboard.reply") return;
        if (String(page) !== "whiteboard") return;

        // Simple de-duplication: skip same action if it arrives within 2s
        const now = Date.now();
        const last = recentReplyRef.current || { action: null, t: 0 };
        if (last.action === d.action && now - last.t < 2000) return;
        recentReplyRef.current = { action: d.action, t: now };

        // Build a friendly command text to display exactly what was drawn
        const toCommaList = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
        let cmdText = d.action || "whiteboard";
        if (d.action === "drawShape") {
          if (Array.isArray(d.shapes) && d.shapes.length) {
            cmdText = `draw ${toCommaList(d.shapes)}${
              d.framed ? " with frame" : ""
            }`;
          } else if (d.shape) {
            cmdText = `draw ${d.shape}`;
          } else {
            cmdText = "draw shape";
          }
        } else if (d.action === "drawFrame") {
          const frameType = d.frameType || "frame";
          const childrenStr =
            Array.isArray(d.children) && d.children.length
              ? ` with ${toCommaList(d.children)}`
              : "";
          cmdText = `draw ${frameType}${childrenStr}`;
        } else if (d.action === "freedraw") {
          cmdText = `freedraw${d.description ? ": " + d.description : ""}`;
        } else if (d.action === "insertVideoByQuery") {
          const q = d.query || d.videoQuery || d.search || "";
          cmdText = q ? `insert video by query \"${q}\"` : "insert video";
        } else if (d.action === "openLibrary") {
          const lib = d.library || d.name || "";
          cmdText = lib ? `open library ${lib}` : "open library";
        } else if (/^insert.*image/i.test(d.action || "")) {
          const q = d.query || d.imageQuery || d.search || "";
          cmdText = q ? `insert image by query \"${q}\"` : "insert image";
        } else if (/^insert.*audio/i.test(d.action || "")) {
          const q = d.query || d.audioQuery || d.search || "";
          cmdText = q ? `insert audio by query \"${q}\"` : "insert audio";
        } else if (/^open.*library/i.test(d.action || "")) {
          const lib = d.library || d.name || "";
          cmdText = lib ? `open library ${lib}` : "open library";
        }

        const parsedResult = {
          originalCommand: cmdText,
          type: d.action || "whiteboard",
          studentName: "",
        };
        const execResult = {
          message: d?.error
            ? String(d.error)
            : `Whiteboard ${cmdText} ${d.handled ? "completed" : "failed"}`,
        };
        addToCommandLog(parsedResult, execResult, !!d.handled);
      } catch (err) {
        /* noop */
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [page]);

  // Load command log from server on component mount
  useEffect(() => {
    (async () => {
      try {
        await fetchCommandLogsFromServer(100);
      } catch (e) {
        console.warn("Failed to load command logs from server:", e);
      }
    })();
  }, []);

  // Refetch logs when log modal opens
  useEffect(() => {
    if (showCommandLog) {
      fetchCommandLogsFromServer(100);
    }
  }, [showCommandLog]);

  // Removed custom outside click handler to avoid conflicts with portal-based
  // dropdowns and overlays rendered outside the dialog content.

  // Filter and search command log
  const filteredCommandLog = commandLog.filter((entry) => {
    const matchesSearch =
      !logSearchTerm ||
      entry.command.toLowerCase().includes(logSearchTerm.toLowerCase()) ||
      entry.studentName.toLowerCase().includes(logSearchTerm.toLowerCase()) ||
      entry.type.toLowerCase().includes(logSearchTerm.toLowerCase());

    const matchesFilter =
      logFilterType === "all" ||
      (logFilterType === "success" && entry.success) ||
      (logFilterType === "failed" && !entry.success) ||
      (logFilterType === "attendance" &&
        [
          "present",
          "absent",
          "late",
          "presentAll",
          "absentAll",
          "lateAll",
        ].includes(entry.type)) ||
      (logFilterType === "navigation" &&
        [
          "goToLesson",
          "nextLesson",
          "previousLesson",
          "navigateLesson",
        ].includes(entry.type)) ||
      (logFilterType === "notes" &&
        ["addNote", "updateNote", "clearNote"].includes(entry.type));

    return matchesSearch && matchesFilter;
  });

  const commandHelpSections = useMemo(
    () => getCommandCatalogForPage(page, t),
    [page, t]
  );

  const floatingMicProps = {
    isVisible,
    micPosition,
    onClick: handleMicClick,
    buttonRef: micButtonRef,
  };

  const controlBarProps = {
    currentStep,
    autoConfirm,
    hasTranscript: Boolean(transcript),
    onPause: stopListening,
    onResume: handleResumeListening,
    onConfirmClick: handleConfirmClick,
    onClearTranscript: clearTranscript,
    onClose: handleCloseWidget,
  };

  const contentProps = {
    currentStep,
    meterBars,
    listening,
    showCommands,
    interimTranscript,
    speechTranscript,
    transcript,
    awaitWakeWord,
    feedbackMessage,
    compactMode,
    onToggleCompactMode: toggleCompactMode,
    onToggleShowCommands: toggleShowCommands,
    onToggleVoiceReply: toggleVoiceReply,
    onToggleAutoConfirm: toggleAutoConfirm,
    voiceReplyEnabled,
    autoConfirm,
    onOpenCommandHelp: openCommandHelp,
    onOpenCommandLog: openCommandLog,
    t,
    handleNoClick,
    handleYesClick,
    isNoProcessing,
  };

  const widgetContainerProps = {
    isVisible,
    widgetRef,
    isDragging,
    compactMode,
    position,
    showCommandLog,
    onMouseDown: handleMouseDown,
    controlBarProps,
    contentProps,
  };

  const handleClearLog = async () => {
    setCommandLog([]);
    await clearServerCommandLogs();
  };

  const commandLogModalProps = {
    open: showCommandLog,
    onOpenChange: (e, data) => setShowCommandLog(data.open),
    onClose: () => setShowCommandLog(false),
    filteredCommandLog,
    logSearchTerm,
    onSearchChange: setLogSearchTerm,
    logFilterType,
    onFilterChange: setLogFilterType,
    commandLog,
    onClearLog: handleClearLog,
    commandLogContentRef,
  };

  const commandHelpModalProps = {
    open: showCommandHelp,
    onOpenChange: (e, data) => setShowCommandHelp(data.open),
    onClose: () => setShowCommandHelp(false),
    sections: commandHelpSections,
    header: t("whatCanISay"),
  };

  // Keep rendering when enabled even if speech recognition is unsupported
  // so Firefox users can still see the widget and an informative message.
  if (!isEnabled) {
    return null;
  }

  return (
    <WidgetLayout
      lastAttendanceDataset={lastAttendanceDataset}
      floatingMicProps={floatingMicProps}
      widgetContainerProps={widgetContainerProps}
      commandLogModalProps={commandLogModalProps}
      commandHelpModalProps={commandHelpModalProps}
    />
  );
};

export default VoiceCommandWidgetV2;
